/**
 * Standard Operating Procedure (SOP) types.
 *
 * Storage: supabase/migrations/00306_sops.sql.
 * Service: services/sopService.ts.
 * Compiler (NL -> rule): services/sopCompiler.ts.
 *
 * Phase 1 only covers authoring + compile + simulator. The runtime engine
 * that consumes `compiledRule` ships in a later phase.
 */

// ── Rule AST ─────────────────────────────────────────────────────────

export type SopOp =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'in'
  | 'not_in'
  | 'present'
  | 'absent'
  | 'matches'
  | 'before'
  | 'after'

export interface SopPredicate {
  field: string
  op: SopOp
  value?: unknown
  /** Human-readable label generated by the compiler. Optional. */
  label?: string
}

export interface SopRuleAll {
  all: SopRule[]
}

export interface SopRuleAny {
  any: SopRule[]
}

export interface SopRuleNot {
  not: SopRule
}

export type SopRule = SopPredicate | SopRuleAll | SopRuleAny | SopRuleNot

export function isPredicate(r: SopRule): r is SopPredicate {
  return typeof (r as SopPredicate).field === 'string'
}
export function isAll(r: SopRule): r is SopRuleAll {
  return Array.isArray((r as SopRuleAll).all)
}
export function isAny(r: SopRule): r is SopRuleAny {
  return Array.isArray((r as SopRuleAny).any)
}
export function isNot(r: SopRule): r is SopRuleNot {
  return !!(r as SopRuleNot).not && typeof (r as SopRuleNot).not === 'object'
}

// ── Scope / trigger / severity ───────────────────────────────────────

export type SopScopeKind = 'global' | 'entity' | 'field' | 'event'
export type SopTriggerKind =
  | 'on_save'
  | 'on_create'
  | 'on_transition'
  | 'on_schedule'
  | 'manual'
export type SopSeverity = 'block' | 'warn' | 'info'

export type NotifyChannelKind = 'in_app' | 'email' | 'slack'

export interface NotifyChannel {
  kind: NotifyChannelKind
  /** Role names for in_app, addresses for email, webhook for slack. */
  roles?: string[]
  to?: string[]
  webhook?: string
}

// ── Main record ──────────────────────────────────────────────────────

export interface Sop {
  id: string
  name: string
  description: string | null
  nlBody: string
  compiledRule: SopRule | null
  compiledAt: string | null
  compiledByModel: string | null
  scopeKind: SopScopeKind
  scopeTarget: string | null
  triggerKind: SopTriggerKind
  severity: SopSeverity
  advisory: boolean
  notifyChannels: NotifyChannel[]
  enabled: boolean
  owner: string | null
  category: string | null
  createdAt: string
  createdBy: string | null
  updatedAt: string
  updatedBy: string | null
}

/** Payload accepted by the create / update API. */
export type SopWrite = Omit<
  Sop,
  'id' | 'createdAt' | 'updatedAt' | 'compiledAt' | 'compiledByModel'
> & {
  id?: string
}

// ── Finding (evaluation output) ──────────────────────────────────────

export interface SopFinding {
  clause: string
  message: string
  fieldPath?: string
  expected?: unknown
  actual?: unknown
}

export interface SopEvaluation {
  id: string
  sopId: string
  entityType: string
  entityId: string
  passed: boolean
  findings: SopFinding[]
  payloadSnapshot: Record<string, unknown> | null
  notified: Array<{ channel: NotifyChannelKind; status: 'sent' | 'failed'; detail?: string }>
  ranAt: string
  ranBy: string | null
  triggeredBy: SopTriggerKind
}

// ── Display helpers ──────────────────────────────────────────────────

export const SCOPE_KIND_LABELS: Record<SopScopeKind, string> = {
  global: 'Global',
  entity: 'Entity',
  field: 'Field',
  event: 'Event',
}

export const TRIGGER_KIND_LABELS: Record<SopTriggerKind, string> = {
  on_save: 'On save',
  on_create: 'On create',
  on_transition: 'On transition',
  on_schedule: 'On schedule',
  manual: 'Manual',
}

export const SEVERITY_LABELS: Record<SopSeverity, string> = {
  block: 'Block',
  warn: 'Warn',
  info: 'Info',
}

export const SEVERITY_COLORS: Record<SopSeverity, string> = {
  block: 'red',
  warn: 'amber',
  info: 'blue',
}

/**
 * Catalog of entity / event targets the simulator + scope picker offer.
 * Adding a domain here doesn't wire it into the runtime engine — it just
 * gives authors a target to point at. Engine wiring is per-domain.
 */
export const SOP_TARGETS: Array<{ value: string; label: string; kind: SopScopeKind }> = [
  // Entity scope
  { value: 'projects', label: 'Projects', kind: 'entity' },
  { value: 'portfolios', label: 'Portfolios', kind: 'entity' },
  { value: 'wbs', label: 'WBS Elements', kind: 'entity' },
  { value: 'risks', label: 'Risks', kind: 'entity' },
  { value: 'contracts', label: 'Contracts', kind: 'entity' },
  { value: 'milestones', label: 'Milestones', kind: 'entity' },
  { value: 'forecasts', label: 'Forecasts', kind: 'entity' },
  { value: 'timesheets', label: 'Timesheets', kind: 'entity' },
  // Field scope (common targets)
  { value: 'projects.roi', label: 'Project · ROI', kind: 'field' },
  { value: 'projects.endDate', label: 'Project · End Date', kind: 'field' },
  { value: 'projects.status', label: 'Project · Status', kind: 'field' },
  { value: 'risks.score', label: 'Risk · Score', kind: 'field' },
  { value: 'contracts.value', label: 'Contract · Value', kind: 'field' },
  // Event scope
  { value: 'workflow.stage.approved', label: 'Workflow · Stage Approved', kind: 'event' },
  { value: 'project.phase.advanced', label: 'Project · Phase Advanced', kind: 'event' },
  { value: 'baseline.locked', label: 'Baseline · Locked', kind: 'event' },
]
